import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatBRL } from "@/lib/api";
import { absUrl } from "@/lib/urls";
import { Heart, Truck, CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const sortSizes = (sizes) => {
  // Try numeric sort first (for "14", "16", "40cm", "2mm"), fallback to natural
  const parse = (s) => {
    const m = String(s).match(/^(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : Number.MAX_SAFE_INTEGER;
  };
  return [...sizes].sort((a, b) => {
    const na = parse(a), nb = parse(b);
    if (na !== nb) return na - nb;
    return String(a).localeCompare(String(b));
  });
};

export default function Product() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [img, setImg] = useState(null);
  const [size, setSize] = useState(null);
  const [variation, setVariation] = useState(null);
  const [qty, setQty] = useState(1);
  const [isFav, setIsFav] = useState(false);
  // Para alianças personalizadas
  const [pair, setPair] = useState({ size_1: "", name_1: "", size_2: "", name_2: "" });
  const { addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => {
      setData(data);
      setImg((data.product.images && data.product.images[0]) || null);
      setSize(data.product.sizes?.[0] || null);
      setVariation(data.product.variations?.[0] || null);
    }).catch(() => setData({ product: null }));
  }, [slug]);

  useEffect(() => {
    if (!user || !data?.product) return;
    api.get("/me/favorites").then(({ data: favs }) => {
      setIsFav(favs.some((p) => p.id === data.product.id));
    }).catch(() => {});
  }, [user, data?.product]);

  if (!data) return <div className="p-20 text-center text-[#49392F]/70">Carregando...</div>;
  const p = data.product;
  if (!p) return <div className="p-20 text-center text-[#49392F]/70">Produto não encontrado</div>;

  const price = p.sale_price || p.price;
  const hasSale = p.sale_price && p.sale_price < p.price;
  const outOfStock = (p.stock || 0) <= 0;

  const toggleFav = async () => {
    if (!user) { toast("Entre na sua conta para favoritar"); navigate("/login"); return; }
    if (isFav) { await api.delete(`/me/favorites/${p.id}`); setIsFav(false); }
    else { await api.post(`/me/favorites/${p.id}`); setIsFav(true); toast.success("Adicionada aos favoritos"); }
  };

  const addToCart = () => {
    if (outOfStock) return;
    if (p.is_custom_pair) {
      if (!pair.size_1 || !pair.size_2 || !pair.name_1.trim() || !pair.name_2.trim()) {
        toast.error("Preencha os dois tamanhos e nomes das alianças"); return;
      }
      addItem(p, qty, null, null, {
        size_1: pair.size_1, name_1: pair.name_1.trim().toUpperCase(),
        size_2: pair.size_2, name_2: pair.name_2.trim().toUpperCase(),
      });
      toast.success(`${p.name} adicionado ao carrinho`);
      return;
    }
    if (p.sizes?.length && !size) { toast.error("Selecione um tamanho"); return; }
    addItem(p, qty, size, variation);
    toast.success(`${p.name} adicionado ao carrinho`);
  };

  const buyNow = () => { addToCart(); navigate("/carrinho"); };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="product-page">
      <nav className="text-[11px] tracking-[0.2em] uppercase text-[#49392F]/60 mb-8">
        <Link to="/" className="hover:text-[#C2A064]">Início</Link> / <Link to="/catalogo" className="hover:text-[#C2A064]">Catálogo</Link> / <span className="text-[#49392F]">{p.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-12">
        <div>
          <div className="aspect-[4/5] bg-white overflow-hidden">
            <img src={absUrl(img) || "https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=1200"} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {p.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-3 mt-4">
              {p.images.map((src, i) => (
                <button key={i} onClick={() => setImg(src)} className={`aspect-square overflow-hidden border ${img === src ? "border-[#49392F]" : "border-transparent"}`}>
                  <img src={absUrl(src)} alt={`${p.name} ${i+1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:sticky lg:top-32 lg:self-start">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#C2A064]">{p.category}</div>
          <h1 className="font-display text-3xl md:text-4xl text-[#49392F] mt-3">{p.name}</h1>
          <div className="mt-5 flex items-baseline gap-3">
            {hasSale ? (
              <>
                <span className="font-display text-3xl text-[#49392F]">{formatBRL(p.sale_price)}</span>
                <span className="text-[14px] text-[#49392F]/50 line-through">{formatBRL(p.price)}</span>
              </>
            ) : (
              <span className="font-display text-3xl text-[#49392F]">{formatBRL(price)}</span>
            )}
          </div>
          <p className="mt-2 text-[12px] text-[#49392F]/70">Material: <span className="text-[#49392F]">{p.material}</span></p>
          {p.description && <p className="mt-6 text-[14px] text-[#49392F]/80 leading-relaxed">{p.description}</p>}

          {p.is_custom_pair ? (
            <div className="mt-8 bg-[#E8DCCB]/30 border border-[#C2A064]/30 p-5" data-testid="custom-pair-form">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#C2A064] mb-3">Alianças personalizadas</div>
              <p className="text-[12px] text-[#49392F]/80 mb-4">Informe o tamanho e o nome a ser gravado em cada aliança.</p>
              {[1, 2].map((n) => (
                <div key={n} className="mb-4 last:mb-0">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-[#49392F]/80 mb-2">Aliança {n}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className="duo-input"
                      value={pair[`size_${n}`]}
                      onChange={(e) => setPair({...pair, [`size_${n}`]: e.target.value})}
                      data-testid={`pair-size-${n}`}
                    >
                      <option value="">Tamanho</option>
                      {(p.sizes?.length ? sortSizes(p.sizes) : []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input
                      className="duo-input"
                      placeholder="Nome a gravar"
                      maxLength={15}
                      value={pair[`name_${n}`]}
                      onChange={(e) => setPair({...pair, [`name_${n}`]: e.target.value})}
                      data-testid={`pair-name-${n}`}
                    />
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-[#49392F]/60">Máximo de 15 caracteres por nome. A gravação é feita após a confirmação do pedido.</p>
            </div>
          ) : (
            p.sizes?.length > 0 && (
              <div className="mt-8">
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F] mb-3">Tamanho</div>
                <div className="flex flex-wrap gap-2" data-testid="size-selector">
                  {sortSizes(p.sizes).map((s) => (
                    <button key={s} onClick={() => setSize(s)} className={`min-w-[44px] h-10 px-3 text-[13px] border ${size === s ? "bg-[#49392F] text-white border-[#49392F]" : "bg-white border-[#E8DCCB] text-[#49392F] hover:border-[#49392F]"}`}>{s}</button>
                  ))}
                </div>
              </div>
            )
          )}

          {p.variations?.length > 0 && (
            <div className="mt-6">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#49392F] mb-3">Variação</div>
              <select value={variation || ""} onChange={(e) => setVariation(e.target.value)} className="duo-input max-w-xs" data-testid="variation-select">
                {p.variations.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-[#E8DCCB]">
              <button onClick={() => setQty((q) => Math.max(1, q-1))} className="w-10 h-11 text-[#49392F]" aria-label="menos">−</button>
              <span className="w-10 text-center text-[14px] text-[#49392F]" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q+1)} className="w-10 h-11 text-[#49392F]" aria-label="mais">+</button>
            </div>
            <span className="text-[12px] text-[#49392F]/60" data-testid="stock-info">
              {outOfStock ? "Esgotado" : `${p.stock} em estoque`}
            </span>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button onClick={addToCart} disabled={outOfStock} className="duo-btn duo-btn-primary flex-1 disabled:opacity-50" data-testid="add-to-cart-button">
              {outOfStock ? "Esgotado" : "Adicionar ao carrinho"}
            </button>
            <button onClick={buyNow} disabled={outOfStock} className="duo-btn duo-btn-secondary flex-1 disabled:opacity-50" data-testid="buy-now-button">
              Comprar agora
            </button>
            <button onClick={toggleFav} className={`h-11 w-11 border flex items-center justify-center ${isFav ? "bg-[#C2A064] text-white border-[#C2A064]" : "bg-white text-[#49392F] border-[#E8DCCB]"}`} data-testid="favorite-button" aria-label="favoritar">
              <Heart size={18} fill={isFav ? "white" : "none"} />
            </button>
          </div>

          <div className="mt-10 pt-8 border-t border-[#C2A064]/20 space-y-4 text-[13px] text-[#49392F]/80">
            <div className="flex items-center gap-3"><Truck size={16} className="text-[#C2A064]" /> Entrega para todo o Brasil — frete consultado ao fechar o pedido.</div>
            <div className="flex items-center gap-3"><CreditCard size={16} className="text-[#C2A064]" /> Pix ou link de pagamento, combinado por WhatsApp após o pedido.</div>
            <div className="flex items-center gap-3"><ShieldCheck size={16} className="text-[#C2A064]" /> Compra protegida e atendimento pessoal.</div>
          </div>
        </div>
      </div>

      {data.related?.length > 0 && (
        <section className="mt-24">
          <h2 className="font-display text-2xl text-[#49392F] mb-8">Você também pode gostar</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {data.related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
